import React, {useEffect} from 'react';
import {Outlet, useLocation} from 'react-router';

import {PathnameProvider} from 'coreHooks/usePathname/PathnameContext';
import {RouterListenerBridge} from 'coreUtils/adapters/router';
import {CorrelationIdProvider} from 'coreUtils/adapters/sdk';

import {AppError, useAppBootstrap} from './AppBootstrapContext';
import AppErrorBoundary from './AppErrorBoundary';
import type {AppLocals} from './types';

type AppConfigComponent = React.ComponentType<{
	children: React.ReactNode;
	locals: AppLocals;
	messages: Record<string, unknown>;
	detectedLanguage?: string;
	envPreferences: Record<string, unknown>;
}>;

type CreateAppRootOptions = {
	/** Brand `app/components/_app-config` (providers: Redux, intl, GeneralProvider, …). */
	AppConfig: AppConfigComponent;
	/** Brand `app/components/_app` (layout: header, footer, effects). */
	App: React.ComponentType<Record<string, unknown> & {children: React.ReactNode}>;
	/** Brand `app/components/_error`. */
	ErrorPage: React.ComponentType<AppError>;
};

/**
 * Builds the default export of a brand `root.tsx`: the equivalent of what the PWA Kit SDK
 * `Switch` + `routeComponent(App)` rendered around the matched route.
 */
const createAppRoot = ({AppConfig, App, ErrorPage}: CreateAppRootOptions) => {
	const AppRoot = () => {
		const {locals, messages, detectedLanguage, envPreferences, appProps, correlationId, error, onHydrate} =
			useAppBootstrap();
		const location = useLocation();

		useEffect(() => {
			onHydrate?.();
		}, []);

		return (
			<CorrelationIdProvider correlationId={correlationId} resetOnPageChange={typeof correlationId === 'function'}>
				<PathnameProvider pathname={location.pathname}>
					<AppConfig
						locals={locals}
						messages={messages}
						detectedLanguage={detectedLanguage}
						envPreferences={envPreferences}
					>
						<RouterListenerBridge />
						<AppErrorBoundary error={error} resetKey={location.pathname} ErrorPage={ErrorPage}>
							<App {...appProps} isLoading={false}>
								<Outlet />
							</App>
						</AppErrorBoundary>
					</AppConfig>
				</PathnameProvider>
			</CorrelationIdProvider>
		);
	};

	return AppRoot;
};

export default createAppRoot;
